import React, { useState, useEffect, useContext } from "react";
import { Button, Spinner, Modal } from "react-bootstrap";
import { format } from "date-fns";
import Swal from "sweetalert2";
import Select from "react-select";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import {
  IconClock,
  IconCalendarEvent,
  IconAlertCircle,
  IconCheck,
  IconX,
  IconPlus,
  IconSettings,
  IconTrash,
  IconEdit,
} from "@tabler/icons-react";
import { api } from "../../api/Middleware";
import { BUSINESS_EXCEPTIONS_URL, BUSINESS_HOURS_URL } from "../../api/Urls";
import UserContext from "../../contexts/UserContext";
import styled from "styled-components";

// Schema para validação das exceções
const exceptionSchema = yup.object().shape({
  date: yup.date().required("Data é obrigatória"),
  exception_type: yup.string().required("Tipo de exceção é obrigatório"),
  title: yup.string().required("Título é obrigatório"),
  description: yup.string(),
  start_time: yup.string().when("exception_type", {
    is: "special_hours",
    then: () =>
      yup
        .string()
        .required("Horário de início é obrigatório para horário especial"),
  }),
  end_time: yup.string().when("exception_type", {
    is: "special_hours",
    then: () =>
      yup
        .string()
        .required("Horário de término é obrigatório para horário especial")
        .test(
          "is-after-start",
          "O horário final deve ser posterior ao inicial",
          function (end_time) {
            const { start_time } = this.parent;
            if (!start_time || !end_time) return true;
            return start_time < end_time;
          }
        ),
  }),
});

// Geração de opções de horário
const generateTimeOptions = () => {
  const options = [];
  for (let hour = 0; hour < 24; hour++) {
    const formattedHour = hour.toString().padStart(2, "0");

    // Gerar intervalos de 10 em 10 minutos (00, 10, 20, 30, 40, 50)
    for (let minute = 0; minute < 60; minute += 10) {
      const formattedMinute = minute.toString().padStart(2, "0");
      const timeValue = `${formattedHour}:${formattedMinute}`;

      options.push({
        value: timeValue,
        label: timeValue,
      });
    }
  }
  return options;
};

const TIME_OPTIONS = generateTimeOptions();

// Styled Components
const Container = styled.div`
  margin: 0;
  padding: 40px 20px;
  min-height: 100vh;
  background: #f8f9fa;

  @media (max-width: 768px) {
    padding: 16px;
    background: #ffffff;
  }
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 16px;
    align-items: stretch;
  }
`;

const Title = styled.h2`
  color: #212529;
  font-weight: 700;
  font-size: 28px;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 12px;

  @media (max-width: 768px) {
    font-size: 21px;
    justify-content: center;
  }
`;

const TitleIcon = styled.div`
  width: 40px;
  height: 40px;
  background: linear-gradient(135deg, #007bff, #0056b3);
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;

  @media (max-width: 768px) {
    width: 36px;
    height: 36px;
  }
`;

const TabContainer = styled.div`
  display: flex;
  background: white;
  border-radius: 12px;
  padding: 4px;
  margin-bottom: 24px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 4px;
  }
`;

const TabButton = styled.button`
  flex: 1;
  background: ${(props) => (props.active ? "#007bff" : "transparent")};
  color: ${(props) => (props.active ? "white" : "#6c757d")};
  border: none;
  border-radius: 8px;
  padding: 12px 16px;
  font-weight: 600;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;

  &:hover {
    background: ${(props) => (props.active ? "#0056b3" : "#f8f9fa")};
    color: ${(props) => (props.active ? "white" : "#495057")};
  }

  @media (max-width: 768px) {
    padding: 14px 16px;
  }
`;

const ContentContainer = styled.div`
  margin-bottom: 80px;
`;

const InfoCard = styled.div`
  background: linear-gradient(135deg, #e3f2fd, #f8f9fa);
  border-radius: 12px;
  padding: 16px;
  margin-bottom: 20px;
  border-left: 4px solid #007bff;

  p {
    margin: 0;
    color: #495057;
    font-size: 14px;
    line-height: 1.5;
  }
`;

const DayCard = styled.div`
  background: white;
  border-radius: 16px;
  padding: 20px;
  margin-bottom: 12px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
  border: 2px solid ${(props) => (props.enabled ? "#007bff" : "transparent")};
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.12);
  }

  @media (max-width: 768px) {
    border-radius: 12px;
    padding: 16px;
    margin-bottom: 8px;
  }
`;

const DayHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${(props) => (props.enabled ? "16px" : "0")};
`;

const DayName = styled.h4`
  font-size: 18px;
  font-weight: 600;
  color: #212529;
  margin: 0;

  @media (max-width: 768px) {
    font-size: 16px;
  }
`;

const CustomToggle = styled.div`
  position: relative;
  width: 60px;
  height: 30px;
  background: ${(props) => (props.enabled ? "#007bff" : "#dee2e6")};
  border-radius: 15px;
  cursor: pointer;
  transition: all 0.3s ease;

  &:before {
    content: "";
    position: absolute;
    top: 3px;
    left: ${(props) => (props.enabled ? "33px" : "3px")};
    width: 24px;
    height: 24px;
    background: white;
    border-radius: 50%;
    transition: all 0.3s ease;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
  }
`;

const IntervalContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const IntervalRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;

  @media (max-width: 768px) {
    flex-wrap: wrap;
    gap: 8px;
  }
`;

const TimeSelectContainer = styled.div`
  flex: 1;
  min-width: 120px;

  @media (max-width: 768px) {
    min-width: 100px;
  }
`;

const IntervalSeparator = styled.span`
  color: #6c757d;
  font-weight: 500;
  font-size: 14px;

  @media (max-width: 768px) {
    display: none;
  }
`;

const IntervalButton = styled.button`
  background: ${(props) => {
    if (props.variant === "add") return "#28a745";
    if (props.variant === "remove") return "#dc3545";
    return "#6c757d";
  }};
  color: white;
  border: none;
  border-radius: 8px;
  padding: 8px;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    transform: scale(1.05);
    filter: brightness(1.1);
  }

  &:active {
    transform: scale(0.95);
  }
`;

const SaveButton = styled.button`
  background: var(--primary);
  color: white;
  border: none;
  border-radius: 12px;
  padding: 14px 24px;
  font-weight: 600;
  font-size: 16px;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 8px;
  box-shadow: 0 4px 12px rgba(0, 123, 255, 0.3);

  &:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(0, 123, 255, 0.4);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  @media (max-width: 768px) {
    width: 100%;
    justify-content: center;
    padding: 16px 24px;
  }
`;

const ExceptionFormContainer = styled.div`
  background: white;
  border-radius: 16px;
  padding: 20px;
  margin-bottom: 20px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);

  @media (max-width: 768px) {
    border-radius: 12px;
    padding: 16px;
  }
`;

const FormTitle = styled.h3`
  font-size: 20px;
  font-weight: 600;
  color: #212529;
  margin-bottom: 20px;
  display: flex;
  align-items: center;
  gap: 8px;

  @media (max-width: 768px) {
    font-size: 18px;
    margin-bottom: 16px;
  }
`;

const FormRow = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 16px;
  margin-bottom: 16px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 12px;
  }
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const Label = styled.label`
  font-weight: 600;
  color: #495057;
  font-size: 14px;
`;

const Input = styled.input`
  padding: 12px 16px;
  border: 2px solid #e9ecef;
  border-radius: 8px;
  font-size: 16px;
  transition: all 0.3s ease;

  &:focus {
    outline: none;
    border-color: #007bff;
    box-shadow: 0 0 0 3px rgba(0, 123, 255, 0.1);
  }

  &.is-invalid {
    border-color: #dc3545;
  }
`;

const TextArea = styled.textarea`
  padding: 12px 16px;
  border: 2px solid #e9ecef;
  border-radius: 8px;
  font-size: 16px;
  transition: all 0.3s ease;
  resize: vertical;
  min-height: 80px;

  &:focus {
    outline: none;
    border-color: #007bff;
    box-shadow: 0 0 0 3px rgba(0, 123, 255, 0.1);
  }
`;

const ErrorMessage = styled.div`
  color: #dc3545;
  font-size: 12px;
  margin-top: 4px;
`;

const ExceptionsContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 16px;
  margin-top: 20px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 12px;
  }
`;

const ExceptionCard = styled.div`
  background: white;
  border-radius: 12px;
  padding: 16px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  border-left: 4px solid
    ${(props) => {
      if (props.type === "holiday") return "#dc3545";
      if (props.type === "closure") return "#ffc107";
      if (props.type === "special_hours") return "#17a2b8";
      return "#6c757d";
    }};
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.12);
  }
`;

const ExceptionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 12px;
`;

const ExceptionTitle = styled.h4`
  font-size: 16px;
  font-weight: 600;
  color: #212529;
  margin: 0;
  margin-bottom: 4px;
`;

const ExceptionDate = styled.div`
  font-size: 14px;
  color: #6c757d;
  font-weight: 500;
`;

const ExceptionBadge = styled.span`
  background: ${(props) => {
    if (props.type === "holiday") return "#dc3545";
    if (props.type === "closure") return "#ffc107";
    if (props.type === "special_hours") return "#17a2b8";
    return "#6c757d";
  }};
  color: white;
  padding: 4px 8px;
  border-radius: 8px;
  font-size: 11px;
  font-weight: 500;
  text-transform: uppercase;
`;

const ExceptionDescription = styled.p`
  color: #6c757d;
  font-size: 14px;
  margin: 8px 0 0 0;
  line-height: 1.4;
`;

const DeleteButton = styled.button`
  background: #dc3545;
  color: white;
  border: none;
  border-radius: 6px;
  padding: 6px;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    background: #c82333;
    transform: scale(1.05);
  }

  &:active {
    transform: scale(0.95);
  }
`;

const FloatingButton = styled.button`
  position: fixed;
  bottom: 24px;
  right: 24px;
  width: 56px;
  height: 56px;
  background: #007bff;
  color: white;
  border: none;
  border-radius: 50%;
  font-size: 24px;
  cursor: pointer;
  box-shadow: 0 4px 16px rgba(0, 123, 255, 0.3);
  transition: all 0.3s ease;
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    background: #0056b3;
    transform: scale(1.05);
    box-shadow: 0 6px 20px rgba(0, 123, 255, 0.4);
  }

  &:active {
    transform: scale(0.95);
  }

  @media (max-width: 768px) {
    bottom: 20px;
    right: 20px;
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 40px 20px;
  color: #6c757d;
  background: white;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);

  h4 {
    color: #495057;
    margin-bottom: 8px;
    font-size: 18px;
    font-weight: 600;
  }

  p {
    margin-bottom: 20px;
    line-height: 1.5;
  }
`;

const EmptyIcon = styled.div`
  font-size: 48px;
  margin-bottom: 16px;
  opacity: 0.6;
`;

const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 20px;
  color: #6c757d;
`;

const BusinessHours = () => {
  const { user } = useContext(UserContext);
  const [loading, setLoading] = useState(false);
  const [businessHours, setBusinessHours] = useState([]);
  const [exceptions, setExceptions] = useState([]);
  const [activeTab, setActiveTab] = useState("regular");
  const [appointmentDuration, setAppointmentDuration] = useState(30);
  const [showExceptionModal, setShowExceptionModal] = useState(false);

  // Estado para controlar os dias e horários
  const [weekDays, setWeekDays] = useState([
    {
      day: 0,
      name: "Segunda-feira",
      enabled: false,
      intervals: [{ start: "08:00", end: "18:00" }],
    },
    {
      day: 1,
      name: "Terça-feira",
      enabled: false,
      intervals: [{ start: "08:00", end: "18:00" }],
    },
    {
      day: 2,
      name: "Quarta-feira",
      enabled: false,
      intervals: [{ start: "08:00", end: "18:00" }],
    },
    {
      day: 3,
      name: "Quinta-feira",
      enabled: false,
      intervals: [{ start: "08:00", end: "18:00" }],
    },
    {
      day: 4,
      name: "Sexta-feira",
      enabled: false,
      intervals: [{ start: "08:00", end: "18:00" }],
    },
    {
      day: 5,
      name: "Sábado",
      enabled: false,
      intervals: [{ start: "08:00", end: "18:00" }],
    },
    {
      day: 6,
      name: "Domingo",
      enabled: false,
      intervals: [{ start: "08:00", end: "18:00" }],
    },
  ]);

  // Form para exceções
  const {
    register: registerException,
    handleSubmit: handleSubmitException,
    control: controlException,
    reset: resetException,
    watch: watchException,
    formState: { errors: errorsException },
  } = useForm({
    resolver: yupResolver(exceptionSchema),
    defaultValues: {
      date: "",
      exception_type: "holiday",
      title: "",
      description: "",
      start_time: "06:00",
      end_time: "23:59",
    },
  });

  const exceptionType = watchException("exception_type");

  // Opções para os tipos de exceção
  const exceptionTypeOptions = [
    { value: "holiday", label: "Feriado" },
    { value: "closure", label: "Fechamento Temporário" },
    { value: "special_hours", label: "Horário Especial" },
  ];

  // Carregar dados iniciais
  useEffect(() => {
    if (user?.account_id) {
      fetchBusinessHours();
      fetchExceptions();
    }
  }, [user?.account_id]);

  // Buscar horários de funcionamento
  const fetchBusinessHours = async () => {
    if (!user?.account_id) return;

    setLoading(true);
    try {
      const response = await api.get(BUSINESS_HOURS_URL, {
        params: {
          account: user.account_id,
        },
      });

      const hoursData = Array.isArray(response.data)
        ? response.data
        : response.data.results || [];

      setBusinessHours(hoursData);

      // Atualizar o estado dos dias da semana com os dados da API
      const updatedWeekDays = [...weekDays];

      // Primeiro, resetar todos os dias
      updatedWeekDays.forEach((day) => {
        day.enabled = false;
        day.intervals = [{ start: "08:00", end: "18:00" }];
      });

      // Depois, atualizar com os dados recebidos
      hoursData.forEach((hour) => {
        const dayIndex = updatedWeekDays.findIndex(
          (d) => d.day === hour.day_of_week
        );

        if (dayIndex >= 0) {
          updatedWeekDays[dayIndex].enabled = hour.is_active;

          // Processar os intervalos
          if (hour.intervals && hour.intervals.length > 0) {
            updatedWeekDays[dayIndex].intervals = hour.intervals.map(
              (interval) => ({
                start: interval.start_time.substring(0, 5),
                end: interval.end_time.substring(0, 5),
              })
            );
          }
        }
      });

      setWeekDays(updatedWeekDays);
    } catch (error) {
      console.error("Erro ao carregar horários de funcionamento:", error);

      if (error.response?.status !== 404) {
        Swal.fire({
          title: "Erro",
          text: "Não foi possível carregar os horários de funcionamento",
          icon: "error",
        });
      }
    } finally {
      setLoading(false);
    }
  };

  // Buscar exceções
  const fetchExceptions = async () => {
    if (!user?.account_id) return;

    setLoading(true);
    try {
      const response = await api.get(BUSINESS_EXCEPTIONS_URL, {
        params: {
          account: user.account_id,
        },
      });

      const exceptionsData = Array.isArray(response.data)
        ? response.data
        : response.data.results || [];

      setExceptions(exceptionsData);
    } catch (error) {
      console.error("Erro ao carregar exceções:", error);

      if (error.response?.status !== 404) {
        Swal.fire({
          title: "Erro",
          text: "Não foi possível carregar as exceções",
          icon: "error",
        });
      }
    } finally {
      setLoading(false);
    }
  };

  // Funções para gerenciar dias da semana
  const toggleDay = (index) => {
    const newDays = [...weekDays];
    newDays[index].enabled = !newDays[index].enabled;
    setWeekDays(newDays);
  };

  const handleTimeChange = (dayIndex, intervalIndex, field, value) => {
    const newDays = [...weekDays];
    newDays[dayIndex].intervals[intervalIndex][field] = value;
    setWeekDays(newDays);
  };

  const addInterval = (dayIndex) => {
    const newDays = [...weekDays];
    newDays[dayIndex].intervals.push({ start: "12:00", end: "18:00" });
    setWeekDays(newDays);
  };

  const removeInterval = (dayIndex, intervalIndex) => {
    const newDays = [...weekDays];
    if (newDays[dayIndex].intervals.length > 1) {
      newDays[dayIndex].intervals.splice(intervalIndex, 1);
      setWeekDays(newDays);
    }
  };

  const saveBusinessHours = async () => {
    if (!user?.account_id) {
      Swal.fire({
        title: "Erro",
        text: "Usuário não identificado",
        icon: "error",
      });
      return;
    }

    setLoading(true);
    try {
      const enabledDays = weekDays.filter((day) => day.enabled);

      if (enabledDays.length === 0) {
        Swal.fire({
          title: "Atenção",
          text: "Nenhum dia foi habilitado para salvar.",
          icon: "warning",
        });
        setLoading(false);
        return;
      }

      const businessHoursPayload = {
        account: user.account_id,
        appointment_duration: appointmentDuration,
        business_hours: enabledDays.map((day) => ({
          day_of_week: day.day,
          is_active: true,
          intervals: day.intervals.map((interval) => ({
            start_time: interval.start,
            end_time: interval.end,
          })),
        })),
      };

      if (businessHours.length > 0) {
        try {
          await api.delete(`${BUSINESS_HOURS_URL}clear/`);
        } catch (error) {
          console.log("Erro ao limpar horários anteriores:", error);
        }
      }

      const response = await api.post(
        `${BUSINESS_HOURS_URL}create_full_config/`,
        businessHoursPayload
      );

      Swal.fire({
        title: "Sucesso",
        text: "Horários de funcionamento salvos com sucesso!",
        icon: "success",
      });

      fetchBusinessHours();
    } catch (error) {
      console.error("Erro ao salvar horários:", error);
      Swal.fire({
        title: "Erro",
        text: `Erro ao salvar horários: ${
          error.response?.data?.detail ||
          error.response?.data?.error ||
          error.message
        }`,
        icon: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  // Adicionar exceção
  const onSubmitException = async (data) => {
    if (!user?.account_id) {
      Swal.fire({
        title: "Erro",
        text: "Usuário não identificado",
        icon: "error",
      });
      return;
    }

    setLoading(true);
    try {
      await api.post(BUSINESS_EXCEPTIONS_URL, {
        ...data,
        account: user.account_id,
        date: format(new Date(data.date), "yyyy-MM-dd"),
      });

      Swal.fire({
        title: "Sucesso",
        text: "Exceção adicionada com sucesso",
        icon: "success",
      });

      resetException();
      setShowExceptionModal(false);
      fetchExceptions();
    } catch (error) {
      console.error("Erro ao adicionar exceção:", error);
      Swal.fire({
        title: "Erro",
        text: "Não foi possível adicionar a exceção",
        icon: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  // Remover exceção
  const removeException = async (id) => {
    const result = await Swal.fire({
      title: "Confirmar exclusão",
      text: "Tem certeza que deseja excluir esta exceção?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Sim, excluir",
      cancelButtonText: "Cancelar",
    });

    if (result.isConfirmed) {
      setLoading(true);
      try {
        await api.delete(`${BUSINESS_EXCEPTIONS_URL}${id}/`);
        Swal.fire({
          title: "Sucesso",
          text: "Exceção removida com sucesso",
          icon: "success",
        });
        fetchExceptions();
      } catch (error) {
        console.error("Erro ao remover exceção:", error);
        Swal.fire({
          title: "Erro",
          text: "Não foi possível remover a exceção",
          icon: "error",
        });
      } finally {
        setLoading(false);
      }
    }
  };

  const getExceptionTypeLabel = (type) => {
    const types = {
      holiday: "Feriado",
      closure: "Fechamento",
      special_hours: "Horário Especial",
    };
    return types[type] || type;
  };

  if (loading && (!weekDays.length || !exceptions.length)) {
    return (
      <Container>
        <LoadingContainer>
          <Spinner animation="border" variant="primary" />
          <p className="mt-3">Carregando configurações...</p>
        </LoadingContainer>
      </Container>
    );
  }

  return (
    <Container>
      <Header>
        <Title>
          <TitleIcon>
            <IconClock size={24} />
          </TitleIcon>
          Horários de Funcionamento
        </Title>
      </Header>

      <TabContainer>
        <TabButton
          active={activeTab === "regular"}
          onClick={() => setActiveTab("regular")}
        >
          <IconClock size={18} />
          Horários Regulares
        </TabButton>
        <TabButton
          active={activeTab === "exceptions"}
          onClick={() => setActiveTab("exceptions")}
        >
          <IconCalendarEvent size={18} />
          Exceções e Feriados
        </TabButton>
      </TabContainer>

      <ContentContainer>
        {activeTab === "regular" && (
          <>
            {/*      <InfoCard>
              <p>
                Configure os dias e horários de funcionamento. Você pode
                adicionar múltiplos intervalos para cada dia (exemplo: para
                representar horário de almoço).
              </p>
            </InfoCard> */}

            {weekDays.map((day, dayIndex) => (
              <DayCard key={dayIndex} enabled={day.enabled}>
                <DayHeader enabled={day.enabled}>
                  <DayName>{day.name}</DayName>
                  <CustomToggle
                    enabled={day.enabled}
                    onClick={() => toggleDay(dayIndex)}
                  />
                </DayHeader>

                {day.enabled && (
                  <IntervalContainer>
                    {day.intervals.map((interval, intervalIndex) => (
                      <IntervalRow key={intervalIndex}>
                        <TimeSelectContainer>
                          <Select
                            options={TIME_OPTIONS}
                            value={TIME_OPTIONS.find(
                              (opt) => opt.value === interval.start
                            )}
                            onChange={(option) =>
                              handleTimeChange(
                                dayIndex,
                                intervalIndex,
                                "start",
                                option.value
                              )
                            }
                            isSearchable={true}
                            classNamePrefix="select"
                            placeholder="Início"
                            menuPortalTarget={document.body}
                            menuPosition="fixed"
                            styles={{
                              menuPortal: (base) => ({
                                ...base,
                                zIndex: 99999,
                              }),
                              menu: (base) => ({
                                ...base,
                                zIndex: 99999,
                              }),
                            }}
                          />
                        </TimeSelectContainer>

                        <IntervalSeparator>às</IntervalSeparator>

                        <TimeSelectContainer>
                          <Select
                            options={TIME_OPTIONS}
                            value={TIME_OPTIONS.find(
                              (opt) => opt.value === interval.end
                            )}
                            onChange={(option) =>
                              handleTimeChange(
                                dayIndex,
                                intervalIndex,
                                "end",
                                option.value
                              )
                            }
                            isSearchable={true}
                            classNamePrefix="select"
                            placeholder="Fim"
                            menuPortalTarget={document.body}
                            menuPosition="fixed"
                            styles={{
                              menuPortal: (base) => ({
                                ...base,
                                zIndex: 99999,
                              }),
                              menu: (base) => ({
                                ...base,
                                zIndex: 99999,
                              }),
                            }}
                          />
                        </TimeSelectContainer>

                        {intervalIndex === 0 ? (
                          <IntervalButton
                            variant="add"
                            onClick={() => addInterval(dayIndex)}
                            title="Adicionar intervalo"
                          >
                            <IconPlus size={16} />
                          </IntervalButton>
                        ) : (
                          <IntervalButton
                            variant="remove"
                            onClick={() =>
                              removeInterval(dayIndex, intervalIndex)
                            }
                            title="Remover intervalo"
                          >
                            <IconX size={16} />
                          </IntervalButton>
                        )}
                      </IntervalRow>
                    ))}
                  </IntervalContainer>
                )}
              </DayCard>
            ))}

            <div style={{ textAlign: "center", marginTop: "24px" }}>
              <SaveButton onClick={saveBusinessHours} disabled={loading}>
                {loading ? (
                  <Spinner size="sm" animation="border" />
                ) : (
                  <IconCheck size={18} />
                )}
                Salvar Horários
              </SaveButton>
            </div>
          </>
        )}

        {activeTab === "exceptions" && (
          <>
            {exceptions.length === 0 ? (
              <EmptyState>
                <EmptyIcon>📅</EmptyIcon>
                <h4>Nenhuma exceção configurada</h4>
                <p>
                  Adicione feriados, fechamentos temporários ou horários
                  especiais que se sobrepõem aos horários regulares.
                </p>
                <Button
                  variant="primary"
                  onClick={() => setShowExceptionModal(true)}
                >
                  Adicionar Primeira Exceção
                </Button>
              </EmptyState>
            ) : (
              <ExceptionsContainer>
                {exceptions.map((exception) => (
                  <ExceptionCard
                    key={exception.id}
                    type={exception.exception_type}
                  >
                    <ExceptionHeader>
                      <div>
                        <ExceptionTitle>{exception.title}</ExceptionTitle>
                        <ExceptionDate>
                          {exception?.date
                            ? exception.date.split("-").reverse().join("/")
                            : ""}
                        </ExceptionDate>
                      </div>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "8px",
                        }}
                      >
                        <ExceptionBadge type={exception.exception_type}>
                          {getExceptionTypeLabel(exception.exception_type)}
                        </ExceptionBadge>
                        <DeleteButton
                          onClick={() => removeException(exception.id)}
                          title="Excluir exceção"
                        >
                          <IconTrash size={14} />
                        </DeleteButton>
                      </div>
                    </ExceptionHeader>

                    {exception.description && (
                      <ExceptionDescription>
                        {exception.description}
                      </ExceptionDescription>
                    )}

                    <ExceptionDescription>
                      Horário: {exception.start_time} às {exception.end_time}
                    </ExceptionDescription>
                  </ExceptionCard>
                ))}
              </ExceptionsContainer>
            )}
          </>
        )}
      </ContentContainer>

      {activeTab === "exceptions" && (
        <FloatingButton
          onClick={() => setShowExceptionModal(true)}
          title="Nova Exceção"
        >
          <IconPlus size={20} />
        </FloatingButton>
      )}

      {/* Modal de Exceção */}
      <Modal
        show={showExceptionModal}
        onHide={() => setShowExceptionModal(false)}
        size="lg"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>
            <IconCalendarEvent size={20} className="me-2" />
            Adicionar Exceção
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <form onSubmit={handleSubmitException(onSubmitException)}>
            <FormRow className="my-2">
              <FormGroup>
                <Label>Data</Label>
                <Input
                  type="date"
                  {...registerException("date")}
                  className={errorsException.date ? "is-invalid" : ""}
                />
                {errorsException.date && (
                  <ErrorMessage>{errorsException.date.message}</ErrorMessage>
                )}
              </FormGroup>

              <FormGroup>
                <Label>Tipo</Label>
                <Controller
                  name="exception_type"
                  control={controlException}
                  render={({ field }) => (
                    <Select
                      {...field}
                      options={exceptionTypeOptions}
                      value={exceptionTypeOptions.find(
                        (opt) => opt.value === field.value
                      )}
                      onChange={(selected) => field.onChange(selected.value)}
                      placeholder="Selecione o tipo"
                      isSearchable={false}
                      classNamePrefix="select"
                    />
                  )}
                />
                {errorsException.exception_type && (
                  <ErrorMessage>
                    {errorsException.exception_type.message}
                  </ErrorMessage>
                )}
              </FormGroup>
            </FormRow>

            <FormGroup>
              <Label>Título</Label>
              <Input
                type="text"
                placeholder="Ex: Feriado de Natal"
                {...registerException("title")}
                className={errorsException.title ? "is-invalid" : ""}
              />
              {errorsException.title && (
                <ErrorMessage>{errorsException.title.message}</ErrorMessage>
              )}
            </FormGroup>

            <FormGroup className="my-2">
              <Label>Descrição (opcional)</Label>
              <TextArea
                rows={3}
                placeholder="Adicione detalhes sobre esta exceção..."
                {...registerException("description")}
              />
            </FormGroup>

            <FormRow className="my-2">
              <FormGroup>
                <Label>Horário de Início</Label>
                <Input
                  type="time"
                  {...registerException("start_time")}
                  className={errorsException.start_time ? "is-invalid" : ""}
                />
                {errorsException.start_time && (
                  <ErrorMessage>
                    {errorsException.start_time.message}
                  </ErrorMessage>
                )}
              </FormGroup>
              <FormGroup>
                <Label>Horário de Término</Label>
                <Input
                  type="time"
                  {...registerException("end_time")}
                  className={errorsException.end_time ? "is-invalid" : ""}
                />
                {errorsException.end_time && (
                  <ErrorMessage>
                    {errorsException.end_time.message}
                  </ErrorMessage>
                )}
              </FormGroup>
            </FormRow>
          </form>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setShowExceptionModal(false)}
          >
            Cancelar
          </Button>
          <Button
            variant="primary"
            onClick={handleSubmitException(onSubmitException)}
            disabled={loading}
          >
            {loading ? (
              <Spinner size="sm" animation="border" className="me-1" />
            ) : (
              <IconCheck size={16} className="me-1" />
            )}
            Adicionar Exceção
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default BusinessHours;
